import React, { Component, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, errorInfo);
    // Enhanced: Log additional context like user ID if available
    const userId = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__?.getFiberRoots?.(0)?.[0]?.current?.memoizedProps?.children?.props?.children?.props?.user?.id || 'unknown';
    console.error('ErrorBoundary triggered with context:', { error, errorInfo, userId });
  }

  handleCopyError = async () => {
    if (this.state.error) {
      try {
        await navigator.clipboard.writeText(`${this.state.error.message}\n\nStack Trace:\n${this.state.error.stack}`);
        // Optional: Show a brief success toast (using console for now, or integrate with sonner)
        console.log('Error copied to clipboard!');
        // You could add a visual indicator here, like a temporary message
      } catch (err) {
        console.error('Failed to copy error to clipboard:', err);
        // Fallback for older browsers: Create a temporary textarea
        const textArea = document.createElement('textarea');
        textArea.value = `${this.state.error.message}\n\nStack Trace:\n${this.state.error.stack}`;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        console.log('Error copied to clipboard using fallback method!');
      }
    }
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Alert className="max-w-md">
            <AlertDescription>
              <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
              <p className="mb-4">{this.state.error?.message || 'Unknown error'}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  variant="outline" 
                  onClick={this.handleCopyError}
                  className="flex items-center space-x-2"
                >
                  <Copy className="h-4 w-4" />
                  Copy Error to Clipboard
                </Button>
                <Button onClick={this.handleReset} className="flex items-center space-x-2">
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Try Again
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;